import { DateOnly } from '@if/core/date'
import {
  EFinancingCompany,
  EFinancingProgram,
  ELoanPurpose,
  EPaymentFrequency,
  EPaymentPlan,
  EProvince,
} from '@if/core/types'
import {
  EBankFailureCode,
  ECoBorrowerRelation,
  EDisbursementTarget,
  EFeeType,
  EFinancingPlan,
  ELoanStatus,
  EMetro2AccountStatus,
  EMetro2ReportingReason,
  EOperationStatus,
  EPaymentMethod,
  EProduct,
} from '@src/types/constants'

export interface DisbursementLineDto {
  productId: EProduct
  amount: number
  description?: string | null
}

export interface FundedProductDto {
  productId: EProduct
  amount: number
}

export interface LoanDto {
  id: number
  financingCompanyId: EFinancingCompany
  financingProgramId: EFinancingProgram
  financingPlanId: EFinancingPlan
  stateIso: EProvince
  originalLoanId: string | null
  contractNumber: string
  loanPurposeId: ELoanPurpose | null
  creditReferenceNumber: number | null
  creditApplicationId: string
  borrowerId: number
  coBorrowerId: number | null
  coBorrowerRelationId: ECoBorrowerRelation | null
  loanOwnerId: number
  term: number
  paymentPlanId: EPaymentPlan
  activationOn: string // DateOnly
  firstPaymentOn: string // DateOnly
  lastPaymentOn: string // DateOnly
  fundedProducts: FundedProductDto[]

  // Other props...
}

export interface LoanOverrideAmountDto {
  amount: number
  effectiveFrom: string // DateOnly
  effectiveTo: string // DateOnly
  createdOn: string // Date
  createdByUserFullname: string
  createdByUserId: string
}

export interface PaymentConfigDto {
  id: string
  frequency: EPaymentFrequency
  effectiveDate: string
  amount: number
  overrideAmounts: LoanOverrideAmountDto[]
}

export interface LoanStatusHistoryDto {
  id: number
  loanStatusId: ELoanStatus
  reasonId: number | null
  updatedBy: number
  updatedOn: string // DateTime
  updatedByFullName: string | null
}

export interface ExpectedPaymentAtDateDto {
  date: string // DateOnly
  principalAmount: number
  interestAmount: number
}

export interface ExpectedPaymentsCalendarDto {
  expectedPayments: ExpectedPaymentAtDateDto[]
}

export interface LoanOwnerHistoryDto {
  loanOwnerId: number
  effectiveDate: string // DateOnly
}

export interface FullLoanDto {
  loan: LoanDto
  paymentConfigs: PaymentConfigDto[]
  loanStatusHistory: LoanStatusHistoryDto[]
  expectedPaymentsCalendar: ExpectedPaymentsCalendarDto
  ownerHistory: LoanOwnerHistoryDto[]
  metro2Reportings: Metro2ReportingDto[]
}

export interface OperationLateInformationResultDto {
  overdueInterest: number
  overduePrincipal: number
  pastdue: number
}

export interface LoanSummaryInformationResultDto {
  daysLate: number
  metro2AccountStatus: EMetro2AccountStatus
  metro2DateFirstDelinquency: string | null // DateOnly
  metro2LateAmount: number
  metro2DateReporting: string | null // DateOnly
  pastduePrincipal: number
  pastdueAndAccruedInterest: number
  outstandingBalance: number
  amountRequiredForPayout: number
  dailyInterest: number
  amountInCompensation: number
  totalPrincipalPaid: number
  totalInterestPaid: number
  totalAdminFeesPaid: number
  totalNsfFeesPaid: number
}

export interface OperationResultDto {
  loanBalance: number
  principalPaid: number
  interestPaid: number
  nsfPaid: number
  otherFeePaid: number
  principalBalance: number
  nsfBalance: number
  otherBalance: number
  overpaymentPaid: number
  overpaymentBalance: number
  // What the operation billed, per money pool: the calendar's columns for an invoice row.
  interestInvoiced: number
  principalInvoiced: number
  nsfInvoiced: number
  otherFeeInvoiced: number
}

export interface OperationInformationsDto {
  simulatedOperationId: string
  operation: OperationDto
  operationResultDto: OperationResultDto
  lateInformationResult: OperationLateInformationResultDto
}

// Operation DTOs
// Numeric discriminator — values must match the backend LoanOperationType enum.
// CreateLoanOperation is the frontend name for the backend's CreateLoanOperation (= 1).
export enum EOperationType {
  CreateLoanOperation = 1,
  CustomerPaymentOperation = 2,
  InvoiceOperation = 3,
  ChangeOwnerOperation = 4,
  ProductRefundOperation = 5,
  LoanClosingOperation = 6,
  LenderPaymentOperation = 7,
  ClosingSettlementOperation = 8,
  ChargeOffOperation = 9,
  LegacyBalanceNormalizationOperation = 10,
}

// Values must match the backend ClosingSettlementMethodType enum.
export enum EClosingSettlementMethodType {
  Loss = 0,
  RefundToClientByCheck = 1,
  RefundToClientByDirectDeposit = 2,
  SmallBalanceWriteOff = 3,
  AccountToAccount = 4,
  RetainedAtDisbursement = 5,
}

interface BaseOperationDto<T extends EOperationType = EOperationType> {
  _internalType: T
  id: number
  effectiveDate: string // Date as string from API
  operationStatusId: EOperationStatus
  note: string | null
  createdBy: number
  updatedBy: number
}

export interface ChangeOwnerOperationDto extends BaseOperationDto<EOperationType.ChangeOwnerOperation> {
  newLoanOwnerId: number
}

export interface CustomerPaymentOperationDto extends BaseOperationDto<EOperationType.CustomerPaymentOperation> {
  financingCompanyId: EFinancingCompany
  amount: number
  paymentMethodId: EPaymentMethod
  chequeNumber: number | null
  bankJobId: number | null
  acpFileLineNumber: number | null
  bankFailureCode: EBankFailureCode | null
  sendSmsReminder: boolean
  bankFailureDate: string | null
  isManualPayment: boolean | null
}

export interface InvoiceOperationDto extends BaseOperationDto<EOperationType.InvoiceOperation> {
  debtInvoices: FundedProductDto[]
  lenderInvoices: FundedProductDto[]
  legacyClosingId: number | null
}

export interface CreateLoanOperationDto extends BaseOperationDto<EOperationType.CreateLoanOperation> {
  financingCompanyId: EFinancingCompany
  payeeId: string
  payeeType: EDisbursementTarget
  lines: DisbursementLineDto[]
  paymentMethodId: EPaymentMethod
  chequeNumber: number | null
  bankFailureCode: EBankFailureCode | null
}

export interface ProductRefundOperationDto extends BaseOperationDto<EOperationType.ProductRefundOperation> {
  productId: EProduct
  amount: number
  paymentMethodId: EPaymentMethod
  chequeNumber: number | null
  bankJobId: number | null
  acpFileLineNumber: number | null
  bankFailureCode: number | null
}

export type LoanClosingOperationDto = BaseOperationDto<EOperationType.LoanClosingOperation>

export type LegacyBalanceNormalizationOperationDto = BaseOperationDto<EOperationType.LegacyBalanceNormalizationOperation>

export interface LenderPaymentOperationDto extends BaseOperationDto<EOperationType.LenderPaymentOperation> {
  amount: number
  payeeId: string
  payeeType: EDisbursementTarget
  bankJobId: number | null
  chequeNumber: number | null
  depotNo: number | null
  acpFileLineNumber: number | null
  bankFailureCode: EBankFailureCode | null
}

export interface ClosingSettlementOperationDto extends BaseOperationDto<EOperationType.ClosingSettlementOperation> {
  legacyClosingId: number
  methodType: EClosingSettlementMethodType | null
  manualDistribution: FundedProductDto[] | null
}

export type ChargeOffOperationDto = BaseOperationDto<EOperationType.ChargeOffOperation>

export type OperationDto =
  | CustomerPaymentOperationDto
  | InvoiceOperationDto
  | ChangeOwnerOperationDto
  | CreateLoanOperationDto
  | ProductRefundOperationDto
  | LoanClosingOperationDto
  | LenderPaymentOperationDto
  | ClosingSettlementOperationDto
  | ChargeOffOperationDto
  | LegacyBalanceNormalizationOperationDto

// Loan Simulation DTOs
export enum ECalendarActionDto {
  UpdatePaymentFrequency = 'UpdatePaymentFrequencyCalendarActionDto',
  MoveCalendar = 'MoveCalendarCalendarActionDto',
  Reset = 'ResetCalendarActionDto',
  EditPayments = 'EditPaymentsCalendarActionDto',
  ChangePaymentStatus = 'ChangePaymentStatusActionDto',
  AddFee = 'AddFeeActionDto',
  EditFees = 'EditFeesActionDto',
  ChangeRegularPmt = 'ChangeRegularPaymentActionDto',
  ChargeOff = 'ChargeOffActionDto',
  DeleteChargeOff = 'DeleteChargeOffActionDto',
}

interface BaseCalendarSimulationActionDto<TActionType extends ECalendarActionDto> {
  actionType: TActionType
}

export interface UpdatePaymentFrequencyCalendarActionDto
  extends BaseCalendarSimulationActionDto<ECalendarActionDto.UpdatePaymentFrequency> {
  frequency: EPaymentFrequency
  firstPaymentDate: string // DateOnly
}

export interface MoveCalendarCalendarActionDto
  extends BaseCalendarSimulationActionDto<ECalendarActionDto.MoveCalendar> {
  fromDate: string // DateOnly
  toDate: string // DateOnly
  reason: string
  paymentToRaiseIds: number[]
}

export interface EditPaymentDto {
  operationId: number
  effectiveDate?: string // DateOnly, null values indicate no change
  amount?: number
  paymentMethodId?: EPaymentMethod
  note?: string | null
  sendSmsReminder?: boolean
}

export interface AddPaymentDto {
  effectiveDate: string // DateOnly
  amount: number
  paymentMethodId: EPaymentMethod
  note?: string | null
  sendSmsReminder: boolean
}

export interface EditPaymentsCalendarActionDto
  extends BaseCalendarSimulationActionDto<ECalendarActionDto.EditPayments> {
  editedPayments: EditPaymentDto[]
  newPayments: AddPaymentDto[]
  deletedPaymentIds: number[]
}

export interface ChangePaymentStatusActionDto
  extends BaseCalendarSimulationActionDto<ECalendarActionDto.ChangePaymentStatus> {
  newStatus: EOperationStatus
  paymentIds: number[]
  transactionDate?: string // DateOnly
  nsfFee?: number
  nsfStatus?: EBankFailureCode
}

export interface AddFeeActionDto extends BaseCalendarSimulationActionDto<ECalendarActionDto.AddFee> {
  effectiveDate: string // DateOnly
  amount: number
  feeType: EFeeType
  note?: string | null
}

export interface EditFeeDto {
  operationId: number
  effectiveDate?: string // DateOnly, null values indicate no change
  amount?: number
  note?: string | null
}

export interface EditFeesActionDto extends BaseCalendarSimulationActionDto<ECalendarActionDto.EditFees> {
  editedFees: EditFeeDto[]
  cancelledFeeIds: number[]
  reappliedFeeIds: number[]
}

export interface ChangeRegularPaymentActionDto
  extends BaseCalendarSimulationActionDto<ECalendarActionDto.ChangeRegularPmt> {
  amount: number
  fromDate: string // DateOnly
}

export interface ChargeOffActionDto extends BaseCalendarSimulationActionDto<ECalendarActionDto.ChargeOff> {
  effectiveDate: string // DateOnly
}

export interface DeleteChargeOffActionDto
  extends BaseCalendarSimulationActionDto<ECalendarActionDto.DeleteChargeOff> {
  operationId: number
}

export type CalendarActionDto =
  | UpdatePaymentFrequencyCalendarActionDto
  | MoveCalendarCalendarActionDto
  | EditPaymentsCalendarActionDto
  | ChangePaymentStatusActionDto
  | AddFeeActionDto
  | EditFeesActionDto
  | ChangeRegularPaymentActionDto
  | ChargeOffActionDto
  | DeleteChargeOffActionDto
  | BaseCalendarSimulationActionDto<ECalendarActionDto.Reset>

export interface SignedCalendarSimulationDto {
  signature: string
  simulation: CalendarSimulationDto
}

export interface ComputeCalendarSimulationActionDto extends Partial<SignedCalendarSimulationDto> {
  action: CalendarActionDto
}

export interface CalendarSimulationDto {
  fullLoan: FullLoanDto
  actions: CalendarActionDto[]
  operationInformations: OperationInformationsDto[]
  simulationExpiresOn: string
  recalculateTransactionFrom: string | null
  loanSummaryInformation: LoanSummaryInformationResultDto
  nextTransientId: number // signed: the backend hands out the transient ids, the frontend only carries it
}

export interface SpreadOverdueAmountDto {
  simulation: CalendarSimulationDto
  paymentsDate: DateOnly[]
}

export interface SpreadOverdueAmountResultDto {
  amountToRaise: number
  lastPaymentValue: number
  lastPaymentDate: DateOnly
}

export interface CalculateNumberOfPaymentsToClearOverdueAmountDto {
  simulation: CalendarSimulationDto
  fromPaymentDate: string // DateOnly
  amountPerPayment: number
}

export interface CalculateNumberOfPaymentsToClearOverdueAmountResultDto {
  numberOfPayments: number
  lastPaymentAmount: number
}

export interface LoanLockDto {
  loanId: string
  /** Config.TUser id of the holder; null when the loan is not locked */
  userId?: number | null
  username?: string | null
  isAcquired: boolean
  /** computed server-side: the active lock belongs to the calling user */
  isOwnedByCurrentUser: boolean
  /** a system process holds the lock; it cannot be force-released from the UI */
  isSystemLock: boolean
  lockedOn?: string | null // DateTime as ISO string
  expiresOn?: string | null // DateTime as ISO string
}

export interface Metro2ReportingDto {
  id: number | null
  metro2OverrideDate: string // DateOnly
  metro2ReportingReason: EMetro2ReportingReason
  description: string | null
  reported: boolean

  createdByUserFullname?: string | null
  createdByUserId?: string | null
  createdOn: string
}
